import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/cn";

export interface MenuProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  onClose: () => void;
  /** Horizontal alignment relative to the anchor. */
  align?: "start" | "end";
  /** Open above ("top") or below ("bottom") the anchor. */
  vertical?: "top" | "bottom";
  className?: string;
  /** Analytics tag scope for the items. The panel is portaled out of its
   *  parent's DOM, so this is the full path, e.g. "sp-account". */
  scope?: string;
  children: React.ReactNode;
}

/** Anchored popover menu (portal + fixed position). Closes on Escape and on
 *  pointerdown outside both the panel and the anchor. */
export function Menu({ anchorEl, open, onClose, align = "start", vertical = "bottom", className, scope, children }: MenuProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<React.CSSProperties>({});

  useLayoutEffect(() => {
    if (!open || !anchorEl) return;
    const r = anchorEl.getBoundingClientRect();
    const s: React.CSSProperties = { position: "fixed", zIndex: 60, minWidth: 200 };
    if (align === "end") s.right = Math.max(8, window.innerWidth - r.right);
    else s.left = r.left;

    // Auto-flip: `vertical` is the PREFERRED direction, but if the menu can't
    // fit that way (e.g. a chat row near the bottom of the sidebar), open the
    // other way when it has more room — so the dropdown is never clipped.
    const gap = 6;
    const margin = 8;
    const menuH = panelRef.current?.offsetHeight ?? 0;
    const spaceBelow = window.innerHeight - r.bottom - gap - margin;
    const spaceAbove = r.top - gap - margin;
    let openTop = vertical === "top";
    if (openTop && spaceAbove < menuH && spaceBelow > spaceAbove) openTop = false;
    else if (!openTop && spaceBelow < menuH && spaceAbove > spaceBelow) openTop = true;

    if (openTop) {
      s.bottom = Math.max(margin, window.innerHeight - r.top + gap);
      s.maxHeight = Math.max(0, r.top - gap - margin);
    } else {
      s.top = r.bottom + gap;
      s.maxHeight = Math.max(0, window.innerHeight - r.bottom - gap - margin);
    }
    s.overflowY = "auto";
    setStyle(s);
  }, [open, anchorEl, align, vertical]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    const onDown = (e: PointerEvent) => {
      const t = e.target as Node;
      if (panelRef.current?.contains(t) || anchorEl?.contains(t)) return;
      onClose();
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("pointerdown", onDown);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("pointerdown", onDown);
    };
  }, [open, onClose, anchorEl]);

  if (!open) return null;
  return createPortal(
    <div
      ref={panelRef}
      role="menu"
      data-ph-scope={scope}
      style={style}
      className={cn(
        // Neutral popover surface — white in light, grey in dark (not the green
        // theme surface), so menus read as plain grey dropdowns.
        "rounded-md bg-popover border border-edge shadow-lg p-1.5",
        className,
      )}
    >
      {children}
    </div>,
    document.body,
  );
}

export interface MenuItemProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  icon?: React.ReactNode;
  danger?: boolean;
  /** Right-aligned hint (shortcut text, chevron). */
  trailing?: React.ReactNode;
}

export function MenuItem({ icon, danger, trailing, className, children, ...rest }: MenuItemProps) {
  return (
    <button
      type="button"
      role="menuitem"
      className={cn(
        "flex w-full items-center gap-2.5 rounded-md px-3 py-2 text-[13.5px] font-medium",
        "cursor-pointer transition-colors text-left",
        // Rest = muted grey; hover/press = accent TEXT on a neutral grey fill
        // (no green background). Danger (Log out) stays red. Icon inherits color.
        danger
          ? "text-danger hover:bg-danger/10"
          : "text-ink-secondary hover:text-accent hover:bg-hover active:text-accent",
        className,
      )}
      {...rest}
    >
      {icon && <span className="shrink-0">{icon}</span>}
      <span className="flex-1 min-w-0 truncate">{children}</span>
      {trailing && <span className="shrink-0 text-ink-faint text-xs">{trailing}</span>}
    </button>
  );
}

export function MenuDivider() {
  return <div role="separator" className="my-1.5 h-px bg-hover" />;
}

/** Non-interactive context row (e.g. the signed-in email atop the account menu). */
export function MenuHeader({ children }: { children: React.ReactNode }) {
  return <div className="px-3 pt-1.5 pb-1 text-xs text-ink-muted truncate">{children}</div>;
}
